import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { postData, ImageFileUpload } from "../utility/Utility";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

function MessageTemplateModal({ isOpen, onClose, selectedDate, festival, onTemplateCreated, editMode = false, templateData = null, onTemplateUpdated }) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        message: "",
        isActive: true,
        sendOption: "both", // email, whatsapp, both
        audience: "both",
        imageUrl: "",
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const editorRef = useRef(null);

    // Custom configuration for CKEditor
    const editorConfiguration = {
        toolbar: {
            items: [
                'heading', '|',
                'bold', 'italic', 'underline', 'strikethrough', '|',
                'fontColor', 'fontBackgroundColor', '|',
                'bulletedList', 'numberedList', '|',
                'alignment', '|',
                'link', 'insertTable', 'blockQuote', '|',
                'undo', 'redo'
            ]
        },
        heading: {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
            ]
        },
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://'
        },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        },
        placeholder: 'Write your message template here...',
        removePlugins: ['Markdown']
    };

    // Helper function to format date to YYYY-MM-DD
    const formatDateToString = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getCharacterCount = () => {
        // Strip HTML tags for character count
        const text = formData.message.replace(/<[^>]*>/g, '');
        return text.length;
    };

    const getWordCount = () => {
        const text = formData.message.replace(/<[^>]*>/g, '');
        const words = text.trim().split(/\s+/).filter(word => word.length > 0);
        return words.length;
    };

    useEffect(() => {
        if (isOpen) {
            const getDefaultTemplate = () => {
                if (festival && selectedDate) {
                    const dateStr = selectedDate.toLocaleDateString('en-IN');
                    return {
                        title: `${festival.name} - Message Template`,
                        message: `Dear,<br><br>Warm wishes on the occasion of ${festival.name}!<br><br>We hope you have a wonderful celebration with your loved ones.<br><br>Best regards,<br>Your Insurance Team`,
                    };
                } else if (selectedDate) {
                    const dateStr = selectedDate.toLocaleDateString('en-IN');
                    return {
                        title: `Message Template`,
                        message: `Dear,<br><br>We hope this message finds you well.<br><br>We are here to serve you with the best insurance solutions tailored to your needs.<br><br>Best regards,<br>Your Insurance Team`,
                    };
                }
                return { title: "", message: "" };
            };

            if (editMode && templateData) {
                // Edit mode - populate form with existing template data
                setFormData({
                    title: templateData.title || "",
                    message: templateData.message || "",
                    isActive: templateData.isActive !== false, // Default to true if not set
                    sendOption: templateData.sendOption || "both",
                    audience: templateData.audience || "both"
                });
            } else {
                // Create mode - use default template
                const defaultTemplate = getDefaultTemplate();
                setFormData(prev => ({
                    ...prev,
                    title: defaultTemplate.title,
                    message: defaultTemplate.message,
                }));
            }
        }
    }, [isOpen, editMode, festival, selectedDate, templateData]);

    // Clean up preview URL when modal closes or file changes
    useEffect(() => {
        if (!isOpen && imagePreview) {
            URL.revokeObjectURL(imagePreview);
            setImagePreview(null);
        }
    }, [isOpen, imagePreview]);

    const validateForm = () => {
        const errors = [];

        if (!formData.title.trim()) {
            errors.push("Please enter template title");
        }

        const plainTextMessage = formData.message.replace(/<[^>]*>/g, '').trim();
        if (!plainTextMessage || formData.message === '<p></p>' || formData.message === '<p><br></p>') {
            errors.push("Please enter template message");
        }

        if (getCharacterCount() > 1000) {
            errors.push("Message exceeds 1000 character limit");
        }

        if (!formData.sendOption) {
            errors.push("Please select send option");
        }

        if (!formData.audience) {
            errors.push("Please select audience");
        }

        return errors;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        // Check file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error("Please upload only JPG, PNG, or GIF images");
            e.target.value = ''; // Clear the input
            return;
        }

        // Check file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            toast.error("File size should be less than 5MB");
            e.target.value = ''; // Clear the input
            return;
        }

        // Validate image dimensions
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            // SAP dimension validation (example: minimum 800x600, maximum 2000x2000)
            const minWidth = 800;
            const minHeight = 600;
            const maxWidth = 2000;
            const maxHeight = 2000;

            if (img.width < minWidth || img.height < minHeight) {
                toast.error(`Image dimensions too small. Minimum size required: ${minWidth}x${minHeight}px. Your image: ${img.width}x${img.height}px`);
                e.target.value = ''; // Clear the input
                return;
            }

            if (img.width > maxWidth || img.height > maxHeight) {
                toast.error(`Image dimensions too large. Maximum size allowed: ${maxWidth}x${maxHeight}px. Your image: ${img.width}x${img.height}px`);
                e.target.value = ''; // Clear the input
                return;
            }

            // If all validations pass, set the file and create preview
            setSelectedFile(file);
            setFormData(prev => ({
                ...prev,
                imageUrl: file.name
            }));

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            toast.error("Error reading image file. Please try a different image.");
            e.target.value = ''; // Clear the input
        };

        img.src = objectUrl;
    };

    const uploadImage = async () => {
        if (!selectedFile) return formData.imageUrl;

        try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', selectedFile);

            const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", uploadFormData);
            if (response?.success) {
                return response?.url || response.data?.imageUrl || response.data?.filePath || formData.imageUrl;
            } else {
                toast.error(response?.message || "Background image upload failed");
                return formData.imageUrl;
            }
        } catch (error) {
            toast.error("Error uploading image");
            console.error("Upload error:", error);
            return formData.imageUrl;
        }
    };

        const handleSubmit = async (e) => {
            e.preventDefault();

            const errors = validateForm();
            if (errors.length > 0) {
                errors.forEach(error => toast.error(error));
                return;
            }

            setLoading(true);

            try {
                // Upload image first if a file is selected
                const imageUrl = await uploadImage();

                const templateDataPayload = {
                    ...formData,
                    imageUrl: imageUrl,
                    festivalName: festival?.name || templateData?.festivalName || null,
                    festivalDate: editMode && templateData ? templateData.festivalDate : formatDateToString(selectedDate),
                    characterCount: getCharacterCount(),
                    wordCount: getWordCount(),
                    createdAt: new Date().toISOString(),
                };

                let response;
                if (editMode && templateData) {
                    // Edit mode - call update API
                    response = await postData('/api/event-templates/create', {
                        eventId: templateData._id,
                        ...templateDataPayload
                    });
                } else {
                    // Create mode - call create API
                    response = await postData('/api/event-templates/create', templateDataPayload);
                }

                if (response?.success) {
                    if (editMode) {
                        toast.success("Message template updated successfully!");
                        if (onTemplateUpdated) {
                            onTemplateUpdated(response.data);
                        }
                    } else {
                        toast.success("Message template created successfully!");
                        // Reset form
                        setFormData({
                            title: "",
                            message: "",
                            isActive: true,
                            sendOption: "both",
                            audience: "both"
                        });

                        // Notify parent component
                        if (onTemplateCreated) {
                            onTemplateCreated(response.data);
                        }
                    }

                    onClose();
                } else {
                    toast.error(response?.message || `Failed to ${editMode ? 'update' : 'create'} message template`);
                }
            } catch (error) {
                toast.error(`Error ${editMode ? 'updating' : 'creating'} message template`);
                console.error(`${editMode ? 'Update' : 'Create'} template error:`, error);
            } finally {
                setLoading(false);
            }
        };

        const handleClose = () => {
            setFormData({
                title: "",
                message: "",
                isActive: true,
                sendOption: "both",
                audience: "both"
            });
            onClose();
        };

        // Get audience summary for display
        const getAudienceSummary = () => {
            switch (formData.audience) {
                case 'employee':
                    return 'Employees Only';
                case 'user':
                    return 'Customers Only';
                case 'both':
                    return 'Both Employees & Customers';
                default:
                    return 'Select Audience';
            }
        };

        // Get channel summary for display
        const getChannelSummary = () => {
            switch (formData.sendOption) {
                case 'email':
                    return 'Email Only';
                case 'whatsapp':
                    return 'WhatsApp Only';
                case 'both':
                    return 'Both Email & WhatsApp';
                default:
                    return 'Select Channel';
            }
        };

        if (!isOpen) return null;

        return (
            <div
                className={`modal fade ${isOpen ? "show d-block" : ""}`}
                tabIndex="-1"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            >
                <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header bg-primary-1 text-white">
                            <h5 className="modal-title">
                                <i className="mdi mdi-email-edit me-2"></i>
                                {editMode ? "Edit Message Template" : "Create Message Template"}
                                {festival && (
                                    <span className="badge bg-light text-primary ms-2">
                                        {festival.name}
                                    </span>
                                )}
                            </h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                onClick={handleClose}
                            ></button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row g-3">
                                    {/* Template Info */}
                                    <div className="col-12">
                                        <div className="alert alert-info">
                                            <i className="mdi mdi-information me-2"></i>
                                            {festival ? (
                                                <>Creating message template for <strong>{festival.name}</strong> on {selectedDate?.toLocaleDateString('en-IN')}</>
                                            ) : (
                                                <>Creating message template for {selectedDate?.toLocaleDateString('en-IN')}</>
                                            )}
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            <i className="mdi mdi-format-title me-1"></i>
                                            Template Title *
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control form-control-lg"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            placeholder="Enter template title"
                                            required
                                        />
                                    </div>

                                    {/* Audience Selection */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            <i className="mdi mdi-account-group me-1"></i>
                                            Send To (Audience) *
                                        </label>
                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <div className={`form-check card p-3 h-100 border ${formData.audience === 'employee' ? 'border-primary bg-light' : ''}`}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="audience"
                                                        id="audienceEmployee"
                                                        value="employee"
                                                        checked={formData.audience === "employee"}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor="audienceEmployee">
                                                        <i className="mdi mdi-account-tie text-primary me-2"></i>
                                                        Employees Only
                                                    </label>
                                                    <small className="text-muted mt-1">
                                                        Send to company employees and staff
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <div className={`form-check card p-3 h-100 border ${formData.audience === 'user' ? 'border-success bg-light' : ''}`}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="audience"
                                                        id="audienceUser"
                                                        value="user"
                                                        checked={formData.audience === "user"}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor="audienceUser">
                                                        <i className="mdi mdi-account text-success me-2"></i>
                                                        Customers Only
                                                    </label>
                                                    <small className="text-muted mt-1">
                                                        Send to customers and clients only
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <div className={`form-check card p-3 h-100 border ${formData.audience === 'both' ? 'border-info bg-light' : ''}`}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="audience"
                                                        id="audienceBoth"
                                                        value="both"
                                                        checked={formData.audience === "both"}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor="audienceBoth">
                                                        <i className="mdi mdi-account-multiple text-info me-2"></i>
                                                        Both Groups
                                                    </label>
                                                    <small className="text-muted mt-1">
                                                        Send to both employees and customers
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Send Options */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            <i className="mdi mdi-send me-1"></i>
                                            Send Via (Channel) *
                                        </label>
                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <div className={`form-check card p-3 h-100 border ${formData.sendOption === 'email' ? 'border-primary bg-light' : ''}`}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="sendOption"
                                                        id="sendEmail"
                                                        value="email"
                                                        checked={formData.sendOption === "email"}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor="sendEmail">
                                                        <i className="mdi mdi-email-outline text-primary me-2"></i>
                                                        Email Only
                                                    </label>
                                                    <small className="text-muted mt-1">
                                                        Send via email messages
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <div className={`form-check card p-3 h-100 border ${formData.sendOption === 'whatsapp' ? 'border-success bg-light' : ''}`}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="sendOption"
                                                        id="sendWhatsApp"
                                                        value="whatsapp"
                                                        checked={formData.sendOption === "whatsapp"}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor="sendWhatsApp">
                                                        <i className="mdi mdi-whatsapp text-success me-2"></i>
                                                        WhatsApp Only
                                                    </label>
                                                    <small className="text-muted mt-1">
                                                        Send via WhatsApp messages
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <div className={`form-check card p-3 h-100 border ${formData.sendOption === 'both' ? 'border-info bg-light' : ''}`}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="sendOption"
                                                        id="sendBoth"
                                                        value="both"
                                                        checked={formData.sendOption === "both"}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor="sendBoth">
                                                        <i className="mdi mdi-email-plus text-info me-2"></i>
                                                        Both Channels
                                                    </label>
                                                    <small className="text-muted mt-1">
                                                        Send via both Email and WhatsApp
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>


                                    {formData.sendOption !== "email" && <div className="col-12">
                                        <label className="form-label">Image</label>
                                        <div className="d-flex align-items-center gap-3">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                className="form-control"
                                                disabled={loading}
                                            />
                                            {selectedFile && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <i className="mdi mdi-image text-success"></i>
                                                    <small className="text-muted">
                                                        {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                                    </small>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => {
                                                            setSelectedFile(null);
                                                            setFormData(prev => ({ ...prev, imageUrl: "" }));
                                                            // Clean up preview URL
                                                            if (imagePreview) {
                                                                URL.revokeObjectURL(imagePreview);
                                                            }
                                                            setImagePreview(null);
                                                        }}
                                                        disabled={loading}
                                                    >
                                                        <i className="mdi mdi-delete"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <small className="text-muted mt-1 d-block">
                                            Supported formats: JPG, PNG, GIF (Max size: 5MB, Min: 800x600px, Max: 2000x2000px)
                                        </small>
                                        {/* Image Preview */}
                                        {imagePreview || formData.imageUrl ? (
                                            <div className="mt-3">
                                                <label className="form-label">Image Preview</label>
                                                <div className="border rounded p-2 bg-light">
                                                    <img
                                                        src={imagePreview || formData.imageUrl}
                                                        alt="Preview"
                                                        className="img-fluid rounded"
                                                        style={{ maxHeight: '200px', maxWidth: '100%' }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div style={{ display: 'none', width: '100%', height: '200px', backgroundColor: '#f8f9fa', borderRadius: '4px', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                                        <i className="mdi mdi-image-off text-muted" style={{ fontSize: '48px' }}></i>
                                                        <small className="text-muted mt-2">No Image Available</small>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-3">
                                                <label className="form-label">Image Preview</label>
                                                <div className="border rounded p-2 bg-light d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                                                    <div className="text-center">
                                                        <i className="mdi mdi-image-plus text-muted" style={{ fontSize: '48px' }}></i>
                                                        <small className="text-muted d-block mt-2">No Image Selected</small>
                                                        <small className="text-muted">Upload an image to see preview</small>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>}
                                    {/* Selection Summary */}
                                    <div className="col-12">
                                        <div className="alert alert-warning">
                                            <i className="mdi mdi-check-all me-2"></i>
                                            <strong>Summary:</strong> This message will be sent to <strong>{getAudienceSummary()}</strong> via <strong>{getChannelSummary()}</strong>
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            <i className="mdi mdi-text me-1"></i>
                                            Message Content *
                                        </label>
                                        <div className="ckeditor-container border rounded">
                                            <CKEditor
                                                editor={ClassicEditor}
                                                data={formData.message}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    setFormData(prev => ({ ...prev, message: data }));
                                                }}
                                                config={editorConfiguration}
                                                onReady={editor => {
                                                    editorRef.current = editor;
                                                }}
                                                onError={(error, { willEditorRestart }) => {
                                                    console.error('CKEditor error:', error);
                                                    if (willEditorRestart) {
                                                        editorRef.current = null;
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="form-text">
                                            Use variables like {'{customer_name}'}, {'{policy_number}'}, {'{expiry_date}'} for personalization
                                        </div>
                                    </div>

                                    {/* Active Status */}
                                    <div className="col-12">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="isActive"
                                                name="isActive"
                                                checked={formData.isActive}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label" htmlFor="isActive">
                                                Active Template (Available for use)
                                            </label>
                                        </div>
                                    </div>

                                    {/* Character count */}
                                    <div className="col-12">
                                        <div className="row mt-4">
                                            <div className="col-12">
                                                <div className="text-muted small">
                                                    <i className="mdi mdi-text me-1"></i>
                                                    Characters: {getCharacterCount()} |
                                                    Words: {getWordCount()}
                                                    {getCharacterCount() > 0 && (
                                                        <span className="ms-2">
                                                            <i className="mdi mdi-check-circle text-success me-1"></i>
                                                            Ready to save
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    <i className="mdi mdi-close me-1"></i>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || !formData.title.trim() || !formData.message.trim() || getCharacterCount() === 0 || !formData.sendOption || !formData.audience}
                                >
                                    {loading ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                            {editMode ? "Updating..." : "Creating..."}
                                        </>
                                    ) : (
                                        <>
                                            <i className="mdi mdi-content-save me-1"></i>
                                            {editMode ? "Update Template" : "Create Template"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

export default MessageTemplateModal;